import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { FileText, Plus, Edit2, Trash2, Download, Check, X, Clock, AlertCircle, Package, Minus, Mail, Paperclip, Upload, Eye, MoreVertical, ArrowUpDown, ArrowUp, ArrowDown, Copy, Search, ChevronDown, ChevronRight } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { computeFiscalSummary, computeAccisaPerBottle, computeContrassegnoPerBottle, formatCurrencyMillesimi } from '../../utils/fiscalCalc';

const AdminQuotes = () => {
  const { adminToken } = useAdminAuth();
  usePageTitle('Preventivi B2B - Admin');
  const [searchParams, setSearchParams] = useSearchParams();
  const [quotes, setQuotes] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [preselectCustomer, setPreselectCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [viewingQuote, setViewingQuote] = useState(null);
  const [editingQuote, setEditingQuote] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState(''); // Ricerca per nome/ragione sociale
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' = più recente in alto, 'asc' = più vecchio in alto
  const [creationStep, setCreationStep] = useState(1); // Step 1: Dati, Step 2: Allegati
  const [newlyCreatedQuoteId, setNewlyCreatedQuoteId] = useState(null); // ID preventivo appena creato
  
  // Paginazione
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalQuotes, setTotalQuotes] = useState(0);
  const quotesPerPage = 20;

  const [formData, setFormData] = useState({
    customer_id: '',
    items: [],
    services: [],
    validity_days: 30,
    terms_and_conditions: 'Ordine minimo 24 bottiglie. Consegna 15 giorni lavorativi. Pagamento anticipato tramite bonifico bancario.',
    admin_notes: '',
    show_discount_table: false,
    payment_schedule_type: 'full',  // 'full' o 'split_50_50'
    shipping_override: null  // null = calcolo automatico, altrimenti valore forzato (0 = gratuita)
  });

  const [attachments, setAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [riepilogoOpen, setRiepilogoOpen] = useState(false);

  const [newItem, setNewItem] = useState({
    description: '',
    details: '',  // Campo descrizione dettagliata
    format: '50cl',
    quantity: 24,
    unit_price: 0,
    unit_cost: 0,
    alcohol_content: 0,
    discount_percent: 0,
    selectedProduct: null,  // Prodotto selezionato dal dropdown
    isManual: false  // Se true, descrizione inserita manualmente
  });

  const [products, setProducts] = useState([]);
  const [formats, setFormats] = useState([]);
  const [discountTiers, setDiscountTiers] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);

  const [newService, setNewService] = useState({
    description: '',
    details: '',
    quantity: 1,
    price: 0,
    discount_percent: 0  // Aggiunto sconto
  });

  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [editingServiceIndex, setEditingServiceIndex] = useState(null);
  
  // Servizi Digital predefiniti
  const [digitalServices, setDigitalServices] = useState([]);
  const [loadingDigitalServices, setLoadingDigitalServices] = useState(false);
  const [digitalServiceSearch, setDigitalServiceSearch] = useState('');
  const [showDigitalServiceDropdown, setShowDigitalServiceDropdown] = useState(false);

  useEffect(() => {
    if (adminToken) {
      loadQuotes(currentPage, searchQuery);
      loadCustomers();
      loadProducts();
      loadFormats();
      loadDiscountTiers();
      loadDigitalServices();
    }
  }, [filterStatus, adminToken, currentPage]);

  // Ricerca con debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (adminToken) {
        setCurrentPage(1);
        loadQuotes(1, searchQuery);
      }
    }, 500); // 500ms debounce
    
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Reset pagina quando cambiano i filtri
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus]);

  // Handler cambio pagina
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Chiudi dropdown quando si clicca fuori
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showProductDropdown && !event.target.closest('.product-dropdown-container')) {
        setShowProductDropdown(false);
      }
      if (showDigitalServiceDropdown && !event.target.closest('.digital-service-dropdown-container')) {
        setShowDigitalServiceDropdown(false);
      }
      if (showCustomerDropdown && !event.target.closest('.customer-dropdown-container')) {
        setShowCustomerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProductDropdown, showDigitalServiceDropdown, showCustomerDropdown]);

  // Ricerca clienti server-side (debounce) quando si digita nel campo cliente
  useEffect(() => {
    const term = customerSearch.trim();
    if (!showModal || formData.customer_id || term.length < 2) return;
    const t = setTimeout(() => searchCustomersServer(term), 300);
    return () => clearTimeout(t);
  }, [customerSearch, showModal, formData.customer_id]);

  // Apri automaticamente modal se c'è quoteId nell'URL
  useEffect(() => {
    const quoteId = searchParams.get('quoteId');
    if (quoteId && quotes.length > 0) {
      const quote = quotes.find(q => q.id === quoteId);
      if (quote) {
        setViewingQuote(quote);
        // Rimuovi il parametro dall'URL dopo averlo usato
        setSearchParams({});
      }
    }
  }, [searchParams, quotes]);

  // Apri modal di modifica se c'è parametro edit nell'URL
  useEffect(() => {
    const editQuoteId = searchParams.get('edit');
    if (editQuoteId && quotes.length > 0) {
      const quote = quotes.find(q => q.id === editQuoteId);
      if (quote) {
        setEditingQuote(quote);
        setFormData({
          customer_id: quote.customer_data.customer_id,
          items: quote.items,
          services: quote.services,
          validity_days: quote.validity_days,
          terms_and_conditions: quote.terms_and_conditions,
          admin_notes: quote.admin_notes || '',
          show_discount_table: quote.show_discount_table !== undefined ? quote.show_discount_table : false,
          payment_schedule_type: quote.payment_schedule_type || 'full',
          shipping_override: quote.shipping_override !== undefined ? quote.shipping_override : null
        });
        setShowModal(true);
        // Rimuovi il parametro dall'URL dopo averlo usato
        setSearchParams({});
      }
    }
  }, [searchParams, quotes]);

  // Apri modal di creazione con cliente preselezionato (da LEAD 2.0)
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      const cid = searchParams.get('customer_id') || '';
      const email = searchParams.get('customer_email') || '';
      resetForm();
      setShowModal(true);
      setPreselectCustomer({ id: cid, email });
      setSearchParams({});
    }
  }, [searchParams]);

  // Risolve il cliente preselezionato una volta caricata la lista clienti
  useEffect(() => {
    if (!preselectCustomer || customers.length === 0) return;
    const { id, email } = preselectCustomer;
    const match = customers.find(c =>
      (email && c.email && c.email.toLowerCase() === email.toLowerCase()) ||
      (id && (c.id === id || String(c._id) === id))
    );
    if (match) {
      setFormData(prev => ({ ...prev, customer_id: match.id }));
      setPreselectCustomer(null);
    }
  }, [preselectCustomer, customers]);

  // Debug e fix del customer_id quando si apre il modal di modifica
  useEffect(() => {
    if (editingQuote && formData.customer_id && customers.length > 0) {
      // Verifica se il customer_id corrisponde a un cliente nella lista
      const customerExists = customers.find(c => c.id === formData.customer_id);
      
      if (!customerExists) {
        console.warn('⚠️ Customer ID nel preventivo non trovato nella lista clienti');
        console.log('Customer ID dal preventivo:', formData.customer_id);
        console.log('IDs disponibili:', customers.map(c => c.id));
        
        // Prova a trovare il cliente usando l'email
        const customerByEmail = customers.find(c => c.email === editingQuote.customer_data.email);
        if (customerByEmail) {
          console.log('✅ Cliente trovato tramite email, aggiorno customer_id');
          setFormData({ ...formData, customer_id: customerByEmail.id });
        }
      } else {
        console.log('✅ Cliente trovato correttamente:', customerExists.email);
      }
    }
  }, [editingQuote, customers]);

  const loadQuotes = async (page = currentPage, search = searchQuery) => {
    try {
      setLoading(true);
      const skip = (page - 1) * quotesPerPage;
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/quotes/admin/quotes?limit=${quotesPerPage}&skip=${skip}`;
      if (filterStatus !== 'all') {
        url += `&status=${filterStatus}`;
      }
      if (search && search.trim()) {
        url += `&search=${encodeURIComponent(search.trim())}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Controlla se la risposta ha struttura paginata
        if (data.data && data.total !== undefined) {
          const sortedQuotes = sortQuotesByDate(data.data, sortOrder);
          setQuotes(sortedQuotes);
          setTotalQuotes(data.total);
          setTotalPages(Math.ceil(data.total / quotesPerPage));
        } else {
          // Fallback per risposta array semplice
          const sortedQuotes = sortQuotesByDate(data, sortOrder);
          setQuotes(sortedQuotes);
          setTotalQuotes(sortedQuotes.length);
          setTotalPages(1);
        }
      }
    } catch (error) {
      console.error('Errore caricamento preventivi:', error);
    } finally {
      setLoading(false);
    }
  };

  // Funzione per ordinare i preventivi per data
  const sortQuotesByDate = (quotesArray, order) => {
    return [...quotesArray].sort((a, b) => {
      const dateA = new Date(a.createdAt || a.created_at);
      const dateB = new Date(b.createdAt || b.created_at);
      return order === 'desc' ? dateB - dateA : dateA - dateB;
    });
  };

  // Cambia ordinamento
  const toggleSortOrder = () => {
    const newOrder = sortOrder === 'desc' ? 'asc' : 'desc';
    setSortOrder(newOrder);
    setQuotes(sortQuotesByDate(quotes, newOrder));
  };

  const loadCustomers = async () => {
    try {
      // Carica clienti business
      const businessResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/admin/users?limit=100&user_type=business`,
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );
      
      // Carica clienti privati
      const privateResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/admin/users?limit=100&user_type=private`,
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );
      
      const businessCustomers = [];
      const privateCustomers = [];
      
      if (businessResponse.ok) {
        const result = await businessResponse.json();
        businessCustomers.push(...(result.data || []));
      }
      
      if (privateResponse.ok) {
        const result = await privateResponse.json();
        privateCustomers.push(...(result.data || []));
      }
      
      // Combina entrambi i tipi
      const allCustomers = [...businessCustomers, ...privateCustomers];
      setCustomers(allCustomers);
      console.log(`Clienti caricati: ${allCustomers.length} totali (Business: ${businessCustomers.length}, Privati: ${privateCustomers.length})`);
      
    } catch (error) {
      console.error('Errore caricamento clienti:', error);
    }
  };

  // Ricerca server-side su TUTTO l'indice utenti (nome, cognome, email, telefono, ragione sociale)
  const searchCustomersServer = async (term) => {
    const q = (term || '').trim();
    if (q.length < 2) return;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/admin/users?search=${encodeURIComponent(q)}&limit=100`,
        { headers: { 'Authorization': `Bearer ${adminToken}` } }
      );
      if (!res.ok) return;
      const data = await res.json();
      const found = data.data || [];
      if (found.length === 0) return;
      setCustomers(prev => {
        const map = new Map(prev.map(c => [c.id, c]));
        found.forEach(c => map.set(c.id, c));
        return Array.from(map.values());
      });
    } catch (e) {
      console.error('Errore ricerca clienti:', e);
    }
  };

  const loadProducts = async () => {
    try {
      setLoadingProducts(true);
      // Usa endpoint pubblico che ritorna tutti i prodotti disponibili
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/products/liquor-types`
      );
      
      if (response.ok) {
        const data = await response.json();
        console.log('🔍 Prodotti caricati:', data?.length || 0, 'prodotti');
        if (data && data.length > 0) {
          console.log('📦 Primi 3 prodotti:', data.slice(0, 3).map(p => p.name));
        }
        setProducts(data || []);
      } else {
        console.error('❌ Errore caricamento prodotti, status:', response.status);
        const errorText = await response.text();
        console.error('Errore dettaglio:', errorText);
      }
    } catch (error) {
      console.error('❌ Errore caricamento prodotti:', error);
    } finally {
      setLoadingProducts(false);
    }
  };

  const loadFormats = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/formats`
      );
      
      if (response.ok) {
        const data = await response.json();
        setFormats(data || []);
      }
    } catch (error) {
      console.error('Errore caricamento formati:', error);
    }
  };

  const loadDiscountTiers = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/discount-tiers`
      );
      
      if (response.ok) {
        const data = await response.json();
        // L'API restituisce direttamente un array, non {tiers: [...]}
        setDiscountTiers(data || []);
        console.log('✅ Discount tiers loaded:', data.length, 'tiers');
      }
    } catch (error) {
      console.error('Errore caricamento sconti:', error);
    }
  };

  const loadDigitalServices = async () => {
    try {
      setLoadingDigitalServices(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/digital-services`,
        {
          headers: { 'Authorization': `Bearer ${adminToken}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setDigitalServices(data || []);
        console.log('✅ Digital services loaded:', data.length);
      }
    } catch (error) {
      console.error('Errore caricamento servizi digital:', error);
    } finally {
      setLoadingDigitalServices(false);
    }
  };

  // Aggiungi servizio digital predefinito al preventivo
  const addDigitalServiceToQuote = (service) => {
    const serviceItem = {
      description: service.name,
      details: service.description || service.short_description || '',  // Descrizione completa nelle note
      quantity: 1,
      price: service.price,
      discount_percent: 0,
      total: service.price,
      digital_service_id: service.id  // Riferimento al servizio originale
    };
    
    setFormData(prev => ({
      ...prev,
      services: [...prev.services, serviceItem]
    }));
    
    // Chiudi dropdown e resetta ricerca
    setDigitalServiceSearch('');
    setShowDigitalServiceDropdown(false);
  };

  // Filtra servizi digital per ricerca
  const filteredDigitalServices = digitalServices.filter(s =>
    s.name.toLowerCase().includes(digitalServiceSearch.toLowerCase()) ||
    (s.category && s.category.toLowerCase().includes(digitalServiceSearch.toLowerCase())) ||
    (s.description && s.description.toLowerCase().includes(digitalServiceSearch.toLowerCase()))
  );

  const calculateAutomaticPrice = (product, formatValue, quantity) => {
    if (!product || !formatValue || !quantity) return 0;

    // Trova il formato selezionato
    const selectedFormat = formats.find(f => `${f.size_cl}cl` === formatValue);
    const formatMultiplier = selectedFormat?.price_multiplier || 1;

    // Calcola il prezzo base (SENZA sconto - lo sconto viene applicato dopo)
    const basePrice = product.basePrice * formatMultiplier;

    return basePrice;
  };

  const getDiscountForQuantity = (quantity) => {
    if (!discountTiers || discountTiers.length === 0) {
      console.log('⚠️  No discount tiers available');
      return 0;
    }

    // Trova la fascia di sconto applicabile
    const applicableTier = discountTiers
      .filter(tier => 
        tier.is_active && 
        quantity >= tier.min_quantity && 
        quantity <= tier.max_quantity
      )
      .sort((a, b) => b.discount_percentage - a.discount_percentage)[0];

    console.log('🔍 getDiscountForQuantity:', {
      quantity,
      tiersCount: discountTiers.length,
      applicableTier: applicableTier ? `${applicableTier.min_quantity}-${applicableTier.max_quantity}: ${applicableTier.discount_percentage * 100}%` : 'none'
    });

    if (applicableTier) {
      return applicableTier.discount_percentage * 100; // Converti in percentuale
    }

    return 0;
  };

  const handleProductSelect = (product) => {
    const calculatedPrice = calculateAutomaticPrice(product, newItem.format, newItem.quantity);
    const discountPercent = getDiscountForQuantity(newItem.quantity);
    const formatKey = newItem.format;
    const costSnapshot = product.industrialCostsByFormat?.[formatKey] || 0;
    const alcoholSnapshot = parseFloat(product.alcoholContent) || 0;
    
    setNewItem({
      ...newItem,
      description: product.name,
      // Pre-fill product default notes only if user hasn't typed anything yet
      details: (newItem.details && newItem.details.trim().length > 0)
        ? newItem.details
        : (product.defaultQuoteNotes || ''),
      selectedProduct: product,
      isManual: false,
      unit_price: calculatedPrice,
      unit_cost: costSnapshot,
      alcohol_content: alcoholSnapshot,
      discount_percent: discountPercent
    });
    setShowProductDropdown(false);
  };

  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    console.log('🔍 Searching for:', value, '| Total products:', products.length);
    
    setNewItem({
      ...newItem,
      description: value,
      isManual: true,
      selectedProduct: null
    });
    
    // Mostra dropdown se ci sono prodotti che matchano
    if (value.length > 1) {
      const matches = products.filter(p => 
        p.name.toLowerCase().includes(value.toLowerCase())
      );
      console.log('✅ Prodotti trovati:', matches.length, matches.map(p => p.name));
      setShowProductDropdown(true);
    } else {
      setShowProductDropdown(false);
    }
  };

  const handleFormatChange = (e) => {
    const newFormat = e.target.value;
    // Calcola il prezzo base per il nuovo formato
    const newPrice = newItem.selectedProduct 
      ? calculateAutomaticPrice(newItem.selectedProduct, newFormat, newItem.quantity)
      : newItem.unit_price;
    // Aggiorna il costo industriale al nuovo formato
    const newCost = newItem.selectedProduct
      ? (newItem.selectedProduct.industrialCostsByFormat?.[newFormat] || 0)
      : newItem.unit_cost;
    
    // Lo sconto rimane lo stesso (dipende solo dalla quantità)
    const discountPercent = getDiscountForQuantity(newItem.quantity);
    
    setNewItem({
      ...newItem,
      format: newFormat,
      unit_price: newPrice,
      unit_cost: newCost,
      discount_percent: discountPercent
    });
  };

  const handleQuantityChange = (e) => {
    const newQuantity = parseInt(e.target.value) || 0;
    
    // Il prezzo base rimane lo stesso (non dipende dalla quantità)
    // Solo lo sconto cambia in base alla quantità
    const discountPercent = getDiscountForQuantity(newQuantity);
    
    console.log('🔍 handleQuantityChange:', {
      newQuantity,
      discountPercent,
      hasSelectedProduct: !!newItem.selectedProduct
    });
    
    setNewItem({
      ...newItem,
      quantity: newQuantity,
      discount_percent: discountPercent
    });
  };

  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(newItem.description.toLowerCase())
  );


  const addItem = () => {
    if (!newItem.description || newItem.quantity <= 0 || newItem.unit_price <= 0) {
      alert('Compila tutti i campi del prodotto');
      return;
    }

    const total = newItem.unit_price * newItem.quantity * (1 - newItem.discount_percent / 100);
    
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { ...newItem, total: parseFloat(total.toFixed(2)) }]
    }));

    setNewItem({
      description: '',
      details: '',
      format: '50cl',
      quantity: 24,
      unit_price: 0,
      unit_cost: 0,
      alcohol_content: 0,
      discount_percent: 0,
      selectedProduct: null,
      isManual: false
    });
    setShowProductDropdown(false);
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const startEditItem = (index) => {
    const item = formData.items[index];
    setNewItem({
      description: item.description,
      details: item.details || '',
      format: item.format,
      quantity: item.quantity,
      unit_price: item.unit_price,
      unit_cost: item.unit_cost || 0,
      alcohol_content: item.alcohol_content || 0,
      discount_percent: item.discount_percent || 0
    });
    setEditingItemIndex(index);
  };

  const saveEditItem = () => {
    if (!newItem.description || newItem.quantity <= 0 || newItem.unit_price <= 0) {
      alert('Compila tutti i campi obbligatori');
      return;
    }

    const total = newItem.unit_price * newItem.quantity * (1 - newItem.discount_percent / 100);

    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === editingItemIndex 
          ? { ...newItem, total: parseFloat(total.toFixed(2)) }
          : item
      )
    }));

    setNewItem({
      description: '',
      details: '',
      format: '50cl',
      quantity: 24,
      unit_price: 0,
      unit_cost: 0,
      alcohol_content: 0,
      discount_percent: 0
    });
    setEditingItemIndex(null);
  };

  const cancelEditItem = () => {
    setNewItem({
      description: '',
      details: '',
      format: '50cl',
      quantity: 24,
      unit_price: 0,
      unit_cost: 0,
      alcohol_content: 0,
      discount_percent: 0
    });
    setEditingItemIndex(null);
  };

  const addService = () => {
    if (!newService.description || newService.price <= 0 || newService.quantity <= 0) {
      alert('Compila tutti i campi del servizio');
      return;
    }

    // Calcola totale con sconto
    const total = newService.price * newService.quantity * (1 - newService.discount_percent / 100);

    setFormData(prev => ({
      ...prev,
      services: [...prev.services, { ...newService, total: parseFloat(total.toFixed(2)) }]
    }));

    setNewService({
      description: '',
      details: '',
      quantity: 1,
      price: 0,
      discount_percent: 0
    });
  };

  const removeService = (index) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index)
    }));
  };

  const startEditService = (index) => {
    const service = formData.services[index];
    setNewService({
      description: service.description,
      details: service.details || '',
      quantity: service.quantity,
      price: service.price,
      discount_percent: service.discount_percent || 0
    });
    setEditingServiceIndex(index);
  };

  const saveEditService = () => {
    if (!newService.description || newService.price <= 0 || newService.quantity <= 0) {
      alert('Compila tutti i campi del servizio');
      return;
    }

    const total = newService.price * newService.quantity * (1 - newService.discount_percent / 100);

    setFormData(prev => ({
      ...prev,
      services: prev.services.map((service, i) => 
        i === editingServiceIndex 
          ? { ...newService, total: parseFloat(total.toFixed(2)) }
          : service
      )
    }));

    setNewService({
      description: '',
      details: '',
      quantity: 1,
      price: 0,
      discount_percent: 0
    });
    setEditingServiceIndex(null);
  };

  const cancelEditService = () => {
    setNewService({
      description: '',
      details: '',
      quantity: 1,
      price: 0,
      discount_percent: 0
    });
    setEditingServiceIndex(null);
  };

  const updateServiceQuantity = (index, newQuantity) => {
    if (newQuantity < 1) return;
    
    setFormData(prev => ({
      ...prev,
      services: prev.services.map((service, i) => {
        if (i === index) {
          const newTotal = service.price * newQuantity;
          return {
            ...service,
            quantity: newQuantity,
            total: parseFloat(newTotal.toFixed(2))
          };
        }
        return service;
      })
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_id) {
      alert('Seleziona un cliente');
      return;
    }

    // Almeno un prodotto O un servizio
    if (formData.items.length === 0 && formData.services.length === 0) {
      alert('Aggiungi almeno un prodotto o un servizio');
      return;
    }

    try {
      const url = editingQuote
        ? `${process.env.REACT_APP_BACKEND_URL}/api/quotes/admin/quotes/${editingQuote.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/quotes/admin/quotes`;
      
      const method = editingQuote ? 'PUT' : 'POST';
      
      // Debug: log del payload
      console.log('📤 Invio preventivo:', {
        method,
        show_discount_table: formData.show_discount_table,
        payload: formData
      });
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        const result = await response.json();
        
        // Se è modifica, chiudi e ricarica
        if (editingQuote) {
          await loadQuotes();
          setShowModal(false);
          resetForm();
          alert('Preventivo aggiornato con successo!');
        } else {
          // Se è creazione, passa allo step 2 (allegati)
          // Il backend restituisce direttamente l'oggetto Quote, non {quote: ...}
          setNewlyCreatedQuoteId(result.id);
          await loadQuotes(); // Ricarica per avere il preventivo nella lista
          setCreationStep(2);
          alert('✅ Preventivo creato! Ora puoi aggiungere gli allegati (mockup bottiglie)');
        }
      } else {
        const error = await response.json();
        alert(error.detail || 'Errore durante il salvataggio');
      }
    } catch (error) {
      console.error('Errore:', error);
      alert('Errore durante il salvataggio');
    }
  };

  const handleDuplicate = async (quote) => {
    if (!window.confirm(`Duplicare il preventivo ${quote.quote_number}?`)) {
      return;
    }

    try {
      // Prepara i dati per la duplicazione (non includere 'id' per items e services)
      const duplicateData = {
        customer_id: quote.customer_id,
        items: quote.items.map(item => ({
          description: item.description,
          details: item.details || '',
          format: item.format,
          quantity: item.quantity,
          unit_price: item.unit_price,
          discount_percent: item.discount_percent || 0,
          total: item.total
        })),
        services: (quote.services || []).map(service => ({
          description: service.description,
          details: service.details || '',
          quantity: service.quantity,
          price: service.price,
          discount_percent: service.discount_percent || 0,
          total: service.total
        })),
        validity_days: quote.validity_days || 30,
        terms_and_conditions: quote.terms_and_conditions || "Consegna 15gg lavorativi previa conferma grafica e ricetta.\nCondizioni di pagamento 50% acconto, 50% a merce pronta per la spedizione.",
        admin_notes: `Duplicato da ${quote.quote_number}`
      };

      console.log('Duplicating quote with data:', duplicateData);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/quotes/admin/quotes`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${adminToken}`
          },
          body: JSON.stringify(duplicateData)
        }
      );

      if (response.ok) {
        await loadQuotes();
        alert('✅ Preventivo duplicato con successo!');
      } else {
        const errorData = await response.json();
        console.error('Errore duplicazione:', errorData);
        alert(`Errore durante la duplicazione: ${errorData.detail || JSON.stringify(errorData)}`);
      }
    } catch (error) {
      console.error('Errore duplicazione:', error);
      alert('Errore durante la duplicazione');
    }
  };

  const handleDelete = async (quoteId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo preventivo?')) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/quotes/admin/quotes/${quoteId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (response.ok) {
        await loadQuotes();
        alert('Preventivo eliminato');
      }
    } catch (error) {
      console.error('Errore eliminazione:', error);
      alert('Errore durante l\'eliminazione');
    }
  };

  const handleExportPDF = async (quoteId, quoteNumber) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/quotes/admin/quotes/${quoteId}/export-pdf`,
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `preventivo_${quoteNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Errore durante l\'export PDF');
      }
    } catch (error) {
      console.error('Errore export PDF:', error);
      alert('Errore durante l\'export PDF');
    }
  };

  const handleSendEmail = async (quoteId, quoteNumber, customerEmail) => {
    if (!window.confirm(`Inviare il preventivo ${quoteNumber} via email a ${customerEmail}?`)) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/quotes/admin/quotes/${quoteId}/send-email`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (response.ok) {
        const result = await response.json();
        alert(`✅ ${result.message}`);
      } else {
        const error = await response.json();
        alert(`❌ ${error.detail || 'Errore durante l\'invio dell\'email'}`);
      }
    } catch (error) {
      console.error('Errore invio email:', error);
      alert('❌ Errore durante l\'invio dell\'email');
    }
  };

  const handleFileUpload = async (event, quoteId) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploadingAttachment(true);

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/quotes/admin/quotes/${quoteId}/attachments`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${adminToken}`
            },
            body: formData
          }
        );

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Errore caricamento file');
        }

        const result = await response.json();
        console.log('File caricato:', result);
      }

      // Reload quote to get updated attachments
      await loadQuotes();
      alert('✅ Allegati caricati con successo');
    } catch (error) {
      console.error('Errore upload allegati:', error);
      alert(`❌ Errore: ${error.message}`);
    } finally {
      setUploadingAttachment(false);
      event.target.value = ''; // Reset input
    }
  };

  const handleDeleteAttachment = async (quoteId, attachmentIndex) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo allegato?')) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/quotes/admin/quotes/${quoteId}/attachments/${attachmentIndex}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Errore eliminazione allegato');
      }

      await loadQuotes();
      alert('✅ Allegato eliminato');
    } catch (error) {
      console.error('Errore eliminazione allegato:', error);
      alert('❌ Errore durante l\'eliminazione');
    }
  };

  // Filtra preventivi in base alla ricerca
  const getFilteredQuotes = () => {
    // La ricerca è ora lato server
    return quotes;
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      items: [],
      services: [],
      validity_days: 30,
      terms_and_conditions: 'Ordine minimo 24 bottiglie. Consegna 15 giorni lavorativi.',
      admin_notes: '',
      show_discount_table: false,
      payment_schedule_type: 'full',
      shipping_override: null
    });
    setNewItem({
      description: '',
      details: '',
      format: '50cl',
      quantity: 24,
      unit_price: 0,
      discount_percent: 0
    });
    setNewService({
      description: '',
      details: '',
      quantity: 1,
      price: 0,
      discount_percent: 0
    });
    setEditingQuote(null);
    setCreationStep(1);
    setNewlyCreatedQuoteId(null);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'In Attesa', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      accepted: { label: 'Accettato', color: 'bg-green-100 text-green-800', icon: Check },
      rejected: { label: 'Rifiutato', color: 'bg-red-100 text-red-800', icon: X },
      modification_requested: { label: 'Modifica Richiesta', color: 'bg-blue-100 text-blue-800', icon: AlertCircle },
      expired: { label: 'Scaduto', color: 'bg-gray-100 text-gray-800', icon: Clock },
      converted_to_order: { label: 'Convertito', color: 'bg-purple-100 text-purple-800', icon: Package }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT');
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Preventivi B2B</h1>
            <p className="text-gray-600 mt-1">Gestisci i preventivi per i clienti</p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="flex items-center space-x-2 bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Plus className="w-5 h-5" />
            <span>Nuovo Preventivo</span>
          </button>
        </div>

        {/* Filtri */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Ricerca */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Cerca per nome, cognome, ragione sociale o numero preventivo..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <label className="text-sm font-medium text-gray-700">Filtra per stato:</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              >
                <option value="all">Tutti</option>
                <option value="pending">In Attesa</option>
                <option value="accepted">Accettati</option>
                <option value="rejected">Rifiutati</option>
                <option value="modification_requested">Modifica Richiesta</option>
                <option value="expired">Scaduti</option>
                <option value="converted_to_order">Convertiti</option>
              </select>
            </div>
            
            {/* Pulsante Ordinamento */}
            <button
              onClick={toggleSortOrder}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              title={sortOrder === 'desc' ? 'Ordina: Più recente in alto' : 'Ordina: Più vecchio in alto'}
            >
              {sortOrder === 'desc' ? (
                <>
                  <ArrowDown className="w-4 h-4 text-gray-600" />
                  <span className="text-sm text-gray-700">Più recente in alto</span>
                </>
              ) : (
                <>
                  <ArrowUp className="w-4 h-4 text-gray-600" />
                  <span className="text-sm text-gray-700">Più vecchio in alto</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Lista Preventivi */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
          </div>
        ) : getFilteredQuotes().length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            {searchQuery ? (
              <>
                <p className="text-gray-600 mb-2">Nessun preventivo trovato per &quot;{searchQuery}&quot;</p>
                <button
                  onClick={() => setSearchQuery('')}
                  className="text-amber-600 hover:text-amber-700 text-sm font-medium"
                >
                  Cancella ricerca
                </button>
              </>
            ) : (
              <p className="text-gray-600">Nessun preventivo trovato</p>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Numero
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cliente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Totale
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    MOL
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Scadenza
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stato
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Azioni
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {getFilteredQuotes().map((quote) => (
                  <tr key={quote.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link 
                        to={`/admin/quotes/${quote.id}`}
                        className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                      >
                        {quote.quote_number}
                      </Link>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{quote.customer_data.business_name}</div>
                      <div className="text-sm text-gray-500">{quote.customer_data.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900">{formatCurrency(quote.total)}</div>
                      <div className="text-xs text-gray-500">IVA incl.</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-testid={`quote-row-mol-${quote.id}`}>
                      {(() => {
                        const items = quote.items || [];
                        const revenue = items.reduce((sum, it) => sum + (it.total || 0), 0);
                        const totalCost = items.reduce((sum, it) => sum + ((it.unit_cost || 0) * (it.quantity || 0)), 0);
                        const fiscal = computeFiscalSummary(items, 'alcohol_content');
                        const hasAnyCost = items.some(it => (it.unit_cost || 0) > 0);
                        const hasFiscal = fiscal.accisa > 0 || fiscal.contrassegno > 0;
                        if ((!hasAnyCost && !hasFiscal) || revenue === 0) {
                          return <span className="text-xs text-gray-400">—</span>;
                        }
                        const molNet = revenue - totalCost - fiscal.accisa - fiscal.contrassegno;
                        const molNetPct = revenue > 0 ? (molNet / revenue) * 100 : 0;
                        const color = molNet >= 0 ? 'text-green-700' : 'text-red-600';
                        return (
                          <div className={color}>
                            <div className="text-sm font-semibold">{formatCurrency(molNet)}</div>
                            <div className="text-xs">{molNetPct.toFixed(1)}%</div>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="font-medium">{formatDate(quote.createdAt || quote.created_at)}</div>
                      <div className="text-xs text-gray-500">Creazione</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(quote.expiry_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(quote.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        {/* Azioni primarie sempre visibili */}
                        <Link
                          to={`/admin/quotes/${quote.id}`}
                          className="text-purple-600 hover:text-purple-900 p-1"
                          title="Vedi dettaglio"
                        >
                          <Eye className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => {
                            setEditingQuote(quote);
                            setFormData({
                              customer_id: quote.customer_data.customer_id,
                              items: quote.items,
                              services: quote.services,
                              validity_days: quote.validity_days,
                              terms_and_conditions: quote.terms_and_conditions,
                              admin_notes: quote.admin_notes || '',
                              show_discount_table: quote.show_discount_table !== undefined ? quote.show_discount_table : false,
                              payment_schedule_type: quote.payment_schedule_type || 'full',
                              shipping_override: quote.shipping_override !== undefined ? quote.shipping_override : null
                            });
                            setShowModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900 p-1"
                          title="Modifica"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        
                        {/* Dropdown per azioni secondarie */}
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDropdown(openDropdown === quote.id ? null : quote.id);
                            }}
                            className="text-gray-600 hover:text-gray-900 p-1"
                            title="Altre azioni"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                          
                          {openDropdown === quote.id && (
                            <>
                              {/* Overlay per chiudere */}
                              <div 
                                className="fixed inset-0 z-10" 
                                onClick={() => setOpenDropdown(null)}
                              />
                              
                              {/* Menu dropdown */}
                              <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                                <button
                                  onClick={() => {
                                    setViewingQuote(quote);
                                    setOpenDropdown(null);
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2"
                                >
                                  <FileText className="w-4 h-4 text-amber-600" />
                                  <span>Anteprima rapida</span>
                                </button>
                                
                                <button
                                  onClick={() => {
                                    handleSendEmail(quote.id, quote.quote_number, quote.customer_data.email);
                                    setOpenDropdown(null);
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2"
                                >
                                  <Mail className="w-4 h-4 text-green-600" />
                                  <span>Invia Email</span>
                                </button>
                                
                                <button
                                  onClick={() => {
                                    handleExportPDF(quote.id, quote.quote_number);
                                    setOpenDropdown(null);
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2"
                                >
                                  <Download className="w-4 h-4 text-purple-600" />
                                  <span>Scarica PDF</span>
                                </button>
                                
                                <button
                                  onClick={() => {
                                    handleDuplicate(quote);
                                    setOpenDropdown(null);
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2"
                                >
                                  <Copy className="w-4 h-4 text-blue-600" />
                                  <span>Duplica</span>
                                </button>
                                
                                <div className="border-t border-gray-100 my-1"></div>
                                
                                <button
                                  onClick={() => {
                                    handleDelete(quote.id);
                                    setOpenDropdown(null);
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 flex items-center space-x-2 text-red-600"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  <span>Elimina</span>
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            
            {/* Paginazione */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Pagina {currentPage} di {totalPages} • {totalQuotes} preventivi totali
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Prima
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    ← Prec
                  </button>
                  
                  {/* Numeri pagina */}
                  <div className="flex gap-1">
                    {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = idx + 1;
                      } else if (currentPage <= 3) {
                        pageNum = idx + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + idx;
                      } else {
                        pageNum = currentPage - 2 + idx;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-8 h-8 text-sm rounded-lg transition-colors ${
                            currentPage === pageNum
                              ? 'bg-blue-600 text-white'
                              : 'border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Succ →
                  </button>
                  <button
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Ultima
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Modal Creazione/Modifica */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                {/* Titolo con indicatore step */}
                <div className="mb-4">
                  <h2 className="text-2xl font-bold">
                    {editingQuote ? 'Modifica Preventivo' : `Nuovo Preventivo - Step ${creationStep}/2`}
                  </h2>
                  {!editingQuote && (
                    <div className="flex items-center space-x-2 mt-2">
                      <div className={`flex items-center ${creationStep >= 1 ? 'text-green-600' : 'text-gray-400'}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${creationStep >= 1 ? 'bg-green-600 text-white' : 'bg-gray-200'}`}>
                          {creationStep > 1 ? '✓' : '1'}
                        </div>
                        <span className="ml-2 text-sm font-medium">Dati Preventivo</span>
                      </div>
                      <div className="flex-1 h-px bg-gray-300 mx-2"></div>
                      <div className={`flex items-center ${creationStep >= 2 ? 'text-green-600' : 'text-gray-400'}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${creationStep >= 2 ? 'bg-green-600 text-white' : 'bg-gray-200'}`}>
                          2
                        </div>
                        <span className="ml-2 text-sm font-medium">Allegati (Mockup)</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* STEP 1: Form Dati Preventivo */}
                {(editingQuote || creationStep === 1) && (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Selezione Cliente */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cliente
                    </label>
                    
                    {editingQuote ? (
                      // Modalità modifica: mostra info cliente read-only
                      <div className="bg-gray-50 border border-gray-300 rounded-lg px-4 py-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-semibold text-gray-900">
                              {editingQuote.customer_data.business_name || 
                               `${editingQuote.customer_data.first_name || ''} ${editingQuote.customer_data.last_name || ''}`.trim() ||
                               'Cliente non disponibile'}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {editingQuote.customer_data.email}
                            </div>
                            {editingQuote.customer_data.vat_number && (
                              <div className="text-xs text-gray-500 mt-0.5">
                                P.IVA: {editingQuote.customer_data.vat_number}
                              </div>
                            )}
                          </div>
                          <div className="text-xs text-gray-400 font-medium">
                            Non modificabile
                          </div>
                        </div>
                      </div>
                    ) : (
                      // Modalità creazione: input cerca + dropdown filtrato
                      (() => {
                        const term = customerSearch.trim().toLowerCase();
                        const matchCustomer = (c) => {
                          if (!term) return true;
                          const company = (c.businessInfo?.companyName || '').toLowerCase();
                          const fn = (c.firstName || '').toLowerCase();
                          const ln = (c.lastName || '').toLowerCase();
                          const email = (c.email || '').toLowerCase();
                          return company.includes(term) || fn.includes(term) || ln.includes(term) || email.includes(term);
                        };
                        const filteredBusiness = customers.filter(c => c.userType === 'business' && matchCustomer(c));
                        const filteredPrivate = customers.filter(c => c.userType === 'private' && matchCustomer(c));
                        const selected = customers.find(c => c.id === formData.customer_id);
                        const selectedLabel = selected
                          ? (selected.userType === 'business'
                              ? `🏢 ${selected.businessInfo?.companyName || 'N/A'} — ${selected.email}`
                              : `👤 ${selected.firstName || ''} ${selected.lastName || ''} — ${selected.email}`)
                          : '';

                        return (
                          <div className="relative customer-dropdown-container">
                            <input
                              type="text"
                              data-testid="customer-search-input"
                              placeholder="Cerca cliente per nome, azienda o email..."
                              value={selected ? selectedLabel : customerSearch}
                              onChange={(e) => {
                                if (selected) setFormData({ ...formData, customer_id: '' });
                                setCustomerSearch(e.target.value);
                                setShowCustomerDropdown(true);
                              }}
                              onFocus={() => setShowCustomerDropdown(true)}
                              className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-transparent ${selected ? 'bg-green-50 font-medium' : ''}`}
                              required={!selected}
                            />
                            {selected && (
                              <button
                                type="button"
                                onClick={() => {
                                  setFormData({ ...formData, customer_id: '' });
                                  setCustomerSearch('');
                                  setShowCustomerDropdown(true);
                                }}
                                data-testid="customer-clear-btn"
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-600"
                                title="Rimuovi selezione"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                            {showCustomerDropdown && !selected && (filteredBusiness.length + filteredPrivate.length > 0) && (
                              <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-72 overflow-y-auto">
                                {filteredBusiness.length > 0 && (
                                  <div>
                                    <div className="px-3 py-1 text-xs font-semibold text-gray-500 bg-gray-50 sticky top-0">🏢 Clienti Business ({filteredBusiness.length})</div>
                                    {filteredBusiness.slice(0, 50).map(c => (
                                      <button
                                        key={c.id}
                                        type="button"
                                        data-testid={`customer-option-${c.id}`}
                                        onClick={() => {
                                          setFormData({ ...formData, customer_id: c.id });
                                          setShowCustomerDropdown(false);
                                          setCustomerSearch('');
                                        }}
                                        className="w-full text-left px-3 py-2 hover:bg-amber-50 border-b border-gray-100 last:border-0"
                                      >
                                        <div className="text-sm font-medium">{c.businessInfo?.companyName || 'N/A'}</div>
                                        <div className="text-xs text-gray-500">
                                          {c.firstName || ''} {c.lastName || ''} · {c.email}
                                        </div>
                                      </button>
                                    ))}
                                  </div>
                                )}
                                {filteredPrivate.length > 0 && (
                                  <div>
                                    <div className="px-3 py-1 text-xs font-semibold text-gray-500 bg-gray-50 sticky top-0">👤 Clienti Privati ({filteredPrivate.length})</div>
                                    {filteredPrivate.slice(0, 50).map(c => (
                                      <button
                                        key={c.id}
                                        type="button"
                                        data-testid={`customer-option-${c.id}`}
                                        onClick={() => {
                                          setFormData({ ...formData, customer_id: c.id });
                                          setShowCustomerDropdown(false);
                                          setCustomerSearch('');
                                        }}
                                        className="w-full text-left px-3 py-2 hover:bg-amber-50 border-b border-gray-100 last:border-0"
                                      >
                                        <div className="text-sm font-medium">{c.firstName || ''} {c.lastName || ''}</div>
                                        <div className="text-xs text-gray-500">{c.email}</div>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                            {showCustomerDropdown && !selected && term && filteredBusiness.length + filteredPrivate.length === 0 && (
                              <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-3 text-sm text-gray-500">
                                Nessun cliente trovato per "{customerSearch}"
                              </div>
                            )}
                          </div>
                        );
                      })()
                    )}
                  </div>

                  {/* Prodotti */}
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Prodotti</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      💡 Tutti i prezzi sono <strong>IVA esclusa</strong>. L&apos;IVA 22% verrà calcolata automaticamente nel totale.
                    </p>
                    
                    {/* Form Aggiunta Prodotto */}
                    <div className="bg-gray-50 p-4 rounded-lg mb-4 space-y-3">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Aggiungi prodotto al preventivo</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
                        <div className="lg:col-span-2 relative product-dropdown-container">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Descrizione prodotto *
                          </label>
                          <input
                            type="text"
                            placeholder="Cerca prodotto o digita manualmente..."
                            value={newItem.description}
                            onChange={handleDescriptionChange}
                            onFocus={() => newItem.description.length > 1 && setShowProductDropdown(true)}
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                          />
                          
                          {/* Dropdown prodotti */}
                          {showProductDropdown && filteredProducts.length > 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                              {filteredProducts.slice(0, 10).map(product => (
                                <button
                                  key={product.id}
                                  type="button"
                                  onClick={() => handleProductSelect(product)}
                                  className="w-full text-left px-3 py-2 hover:bg-purple-50 border-b border-gray-100 last:border-0"
                                >
                                  <div className="font-medium text-sm">{product.name}</div>
                                  <div className="text-xs text-gray-500">
                                    Prezzo base: €{product.basePrice.toFixed(2)} | {product.category}
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                          
                          {/* Indicatore prodotto selezionato */}
                          {newItem.selectedProduct && (
                            <div className="text-xs text-green-600 mt-1">
                              ✓ Prodotto da catalogo: {newItem.selectedProduct.name}
                            </div>
                          )}
                          {newItem.isManual && newItem.description && (
                            <div className="text-xs text-amber-600 mt-1">
                              ✏️ Prodotto manuale (prezzo da inserire manualmente)
                            </div>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Formato bottiglia *
                          </label>
                          <select
                            value={newItem.format}
                            onChange={handleFormatChange}
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                          >
                            {formats.length > 0 ? (
                              formats.map(fmt => (
                                <option key={fmt.id} value={`${fmt.size_cl}cl`}>
                                  {fmt.size_cl}cl - {fmt.name} (x{fmt.price_multiplier})
                                </option>
                              ))
                            ) : (
                              <>
                                <option value="5cl">5cl (miniatura)</option>
                                <option value="20cl">20cl (piccola)</option>
                                <option value="50cl">50cl (standard)</option>
                                <option value="100cl">100cl (magnum)</option>
                              </>
                            )}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Quantità *
                          </label>
                          <input
                            type="number"
                            placeholder="es. 24"
                            value={newItem.quantity}
                            onChange={handleQuantityChange}
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                            min="1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Prezzo unitario * <span className="text-amber-600">(IVA esclusa)</span>
                          </label>
                          <input
                            type="number"
                            placeholder="es. 10.00"
                            step="0.01"
                            value={newItem.unit_price}
                            onChange={(e) => !newItem.selectedProduct && setNewItem({ ...newItem, unit_price: parseFloat(e.target.value) || 0 })}
                            className={`w-full border border-gray-300 rounded px-3 py-2 text-sm ${
                              newItem.selectedProduct ? 'bg-gray-100 cursor-not-allowed' : ''
                            }`}
                            min="0"
                            readOnly={!!newItem.selectedProduct}
                            title={newItem.selectedProduct ? 'Prezzo calcolato automaticamente dal prodotto selezionato' : 'Inserisci prezzo manualmente'}
                          />
                          {newItem.selectedProduct && (
                            <div className="text-xs text-gray-500 mt-1">
                              🔒 Calcolato automaticamente (base: €{newItem.selectedProduct.basePrice.toFixed(2)})
                            </div>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Sconto % {newItem.selectedProduct ? '' : <span className="text-gray-500">(opzionale)</span>}
                          </label>
                          <input
                            type="number"
                            placeholder="es. 10"
                            value={newItem.discount_percent}
                            onChange={(e) => !newItem.selectedProduct && setNewItem({ ...newItem, discount_percent: parseFloat(e.target.value) || 0 })}
                            className={`w-full border border-gray-300 rounded px-3 py-2 text-sm ${
                              newItem.selectedProduct ? 'bg-gray-100 cursor-not-allowed' : ''
                            }`}
                            min="0"
                            max="100"
                            readOnly={!!newItem.selectedProduct}
                            title={newItem.selectedProduct ? 'Sconto applicato automaticamente in base alla quantità' : 'Inserisci sconto manualmente'}
                          />
                          {newItem.selectedProduct && newItem.discount_percent > 0 && (
                            <div className="text-xs text-green-600 mt-1">
                              ✓ Sconto quantità applicato automaticamente
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Campo Descrizione Dettagliata */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Note / Descrizione dettagliata <span className="text-gray-500">(opzionale)</span>
                        </label>
                        <textarea
                          placeholder="es. Etichetta personalizzata con logo aziendale, ricetta speciale con botaniche premium..."
                          value={newItem.details}
                          onChange={(e) => setNewItem({ ...newItem, details: e.target.value })}
                          className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                          rows="2"
                        />
                      </div>

                      {/* Costo industriale per MOL + Gradazione alcolica */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Costo industriale unitario (€) <span className="text-gray-500">— MOL</span>
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            data-testid="item-unit-cost"
                            value={newItem.unit_cost || ''}
                            onChange={(e) => setNewItem({ ...newItem, unit_cost: parseFloat(e.target.value) || 0 })}
                            placeholder="0.00"
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Gradazione alcolica (%) <span className="text-gray-500">— Accisa</span>
                            {newItem.selectedProduct && (
                              <span className="ml-1 text-blue-600">
                                (base prodotto: {parseFloat(newItem.selectedProduct.alcoholContent) || 0}%)
                              </span>
                            )}
                          </label>
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            max="100"
                            data-testid="item-alcohol-content"
                            value={newItem.alcohol_content || ''}
                            onChange={(e) => setNewItem({ ...newItem, alcohol_content: parseFloat(e.target.value) || 0 })}
                            placeholder="es. 40"
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                          />
                        </div>
                      </div>
                      
                      {/* Anteprima calcolo */}
                      {newItem.description && newItem.quantity > 0 && newItem.unit_price > 0 && (
                        <div className="bg-white border border-gray-200 rounded p-3 text-sm">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">Totale riga (IVA esclusa):</span>
                            <span className="font-semibold text-gray-900">
                              {formatCurrency(newItem.unit_price * newItem.quantity * (1 - newItem.discount_percent / 100))}
                            </span>
                          </div>
                          {newItem.discount_percent > 0 && (
                            <div className="text-xs text-gray-500 mt-1">
                              Prezzo pieno: {formatCurrency(newItem.unit_price * newItem.quantity)} - Sconto {newItem.discount_percent}%
                            </div>
                          )}
                          {newItem.unit_cost > 0 && (() => {
                            const revenue = newItem.unit_price * newItem.quantity * (1 - newItem.discount_percent / 100);
                            const cost = newItem.unit_cost * newItem.quantity;
                            const mol = revenue - cost;
                            const molPct = revenue > 0 ? (mol / revenue) * 100 : 0;
                            const color = mol >= 0 ? 'text-green-700' : 'text-red-600';
                            return (
                              <div className={`flex justify-between items-center mt-2 pt-2 border-t text-xs ${color}`}>
                                <span className="font-medium">MOL stimato:</span>
                                <span className="font-bold">{formatCurrency(mol)} ({molPct.toFixed(1)}%)</span>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                      
                      {editingItemIndex !== null ? (
                        <div className="flex space-x-2">
                          <button
                            type="button"
                            onClick={saveEditItem}
                            className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm transition-colors"
                          >
                            ✓ Salva Modifica
                          </button>
                          <button
                            type="button"
                            onClick={cancelEditItem}
                            className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded text-sm transition-colors"
                          >
                            ✕ Annulla
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={addItem}
                          className="w-full bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded text-sm transition-colors"
                        >
                          ➕ Aggiungi Prodotto
                        </button>
                      )}
                    </div>

                    {/* Lista Prodotti Aggiunti */}
                    {formData.items.length > 0 && (
                      <div className="space-y-2">
                        {formData.items.map((item, index) => (
                          <div key={index} className="bg-white border border-gray-200 rounded-lg p-3">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="font-medium">{item.description}</div>
                                <div className="text-sm text-gray-600">
                                  {item.format} × {item.quantity}
                                  {item.discount_percent > 0 ? (
                                    <>
                                      {' '}@ <span className="line-through text-gray-400">{formatCurrency(item.unit_price)}</span>
                                      {' '}<span className="text-green-600 font-semibold">{formatCurrency(item.unit_price * (1 - item.discount_percent / 100))}</span>
                                      {' '}(-{item.discount_percent}%)
                                    </>
                                  ) : (
                                    <> @ {formatCurrency(item.unit_price)}</>
                                  )}
                                </div>
                                {item.details && (
                                  <div className="text-xs text-gray-500 mt-1 italic">
                                    📝 {item.details}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center space-x-3">
                                <div className="text-lg font-semibold">{formatCurrency(item.total)}</div>
                                <button
                                  type="button"
                                  onClick={() => startEditItem(index)}
                                  className="text-blue-600 hover:text-blue-800"
                                  title="Modifica prodotto"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const { id: _omit, ...rest } = item;
                                    const cloned = { ...rest };
                                    const newItems = [...formData.items];
                                    newItems.splice(index + 1, 0, cloned);
                                    setFormData({ ...formData, items: newItems });
                                  }}
                                  data-testid={`clone-item-modal-${index}`}
                                  className="text-purple-600 hover:text-purple-800"
                                  title="Clona prodotto"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removeItem(index)}
                                  className="text-red-600 hover:text-red-800"
                                  title="Elimina prodotto"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Servizi Aggiuntivi */}
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Servizi Aggiuntivi (opzionale)</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Aggiungi servizi extra come consegna express, imballaggio speciale, ecc.
                    </p>
                    
                    <div className="bg-gray-50 p-4 rounded-lg mb-4 space-y-3">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Aggiungi servizio al preventivo</h4>
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Descrizione servizio *
                          </label>
                          <input
                            type="text"
                            placeholder="es. Consegna Express, Imballaggio Premium"
                            value={newService.description}
                            onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Quantità *
                          </label>
                          <input
                            type="number"
                            placeholder="es. 1"
                            value={newService.quantity}
                            onChange={(e) => setNewService({ ...newService, quantity: parseInt(e.target.value) || 1 })}
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                            min="1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Prezzo unitario * <span className="text-amber-600">(IVA esclusa)</span>
                          </label>
                          <input
                            type="number"
                            placeholder="es. 50.00"
                            step="0.01"
                            value={newService.price}
                            onChange={(e) => setNewService({ ...newService, price: parseFloat(e.target.value) || 0 })}
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                            min="0"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Sconto % <span className="text-gray-500">(opzionale)</span>
                          </label>
                          <input
                            type="number"
                            placeholder="es. 10"
                            value={newService.discount_percent}
                            onChange={(e) => setNewService({ ...newService, discount_percent: parseFloat(e.target.value) || 0 })}
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                            min="0"
                            max="100"
                          />
                        </div>
                      </div>
                      
                      {/* Campo Note Servizio */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Note servizio <span className="text-gray-500">(opzionale)</span>
                        </label>
                        <textarea
                          placeholder="es. Consegna entro 24h, giorni lavorativi..."
                          value={newService.details}
                          onChange={(e) => setNewService({ ...newService, details: e.target.value })}
                          className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                          rows="2"
                        />
                      </div>
                      
                      {/* Anteprima calcolo servizio con sconto */}
                      {newService.description && newService.quantity > 0 && newService.price > 0 && (
                        <div className="bg-white border border-gray-200 rounded p-3 text-sm">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">Totale servizio (IVA esclusa):</span>
                            <span className="font-semibold text-gray-900">
                              {formatCurrency(newService.price * newService.quantity * (1 - newService.discount_percent / 100))}
                            </span>
                          </div>
                          {newService.discount_percent > 0 && (
                            <div className="text-xs text-gray-500 mt-1">
                              Prezzo pieno: {formatCurrency(newService.price * newService.quantity)} - Sconto {newService.discount_percent}%
                            </div>
                          )}
                        </div>
                      )}
                      
                      {editingServiceIndex !== null ? (
                        <div className="flex space-x-2">
                          <button
                            type="button"
                            onClick={saveEditService}
                            className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm transition-colors"
                          >
                            ✓ Salva Modifica
                          </button>
                          <button
                            type="button"
                            onClick={cancelEditService}
                            className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded text-sm transition-colors"
                          >
                            ✕ Annulla
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={addService}
                          className="w-full bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded text-sm transition-colors"
                        >
                          ➕ Aggiungi Servizio
                        </button>
                      )}
                    </div>

                    {/* Servizi Digital - Ricerca */}
                    {digitalServices.length > 0 && (
                      <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg digital-service-dropdown-container">
                        <h4 className="text-sm font-medium text-purple-800 mb-3">
                          🖥️ Aggiungi Servizio Digital
                        </h4>
                        <div className="relative">
                          <input
                            type="text"
                            value={digitalServiceSearch}
                            onChange={(e) => {
                              setDigitalServiceSearch(e.target.value);
                              setShowDigitalServiceDropdown(true);
                            }}
                            onFocus={() => setShowDigitalServiceDropdown(true)}
                            placeholder="Cerca servizio digital..."
                            className="w-full border border-purple-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          />
                          
                          {showDigitalServiceDropdown && (
                            <div className="absolute z-20 w-full mt-1 bg-white border border-purple-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                              {filteredDigitalServices.length > 0 ? (
                                filteredDigitalServices.map(service => (
                                  <button
                                    key={service.id}
                                    type="button"
                                    onClick={() => addDigitalServiceToQuote(service)}
                                    className="w-full text-left px-4 py-3 hover:bg-purple-50 border-b border-gray-100 last:border-b-0 transition-colors"
                                  >
                                    <div className="flex justify-between items-start">
                                      <div className="flex-1">
                                        <div className="font-medium text-gray-900">{service.name}</div>
                                        {service.short_description && (
                                          <div className="text-xs text-gray-500 mt-0.5">{service.short_description}</div>
                                        )}
                                        {service.category && (
                                          <span className="inline-block mt-1 text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded">
                                            {service.category}
                                          </span>
                                        )}
                                      </div>
                                      <div className="text-right ml-3">
                                        <div className="font-semibold text-purple-600">€{service.price?.toFixed(2)}</div>
                                        <div className="text-xs text-gray-400">+ IVA</div>
                                      </div>
                                    </div>
                                  </button>
                                ))
                              ) : (
                                <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                  Nessun servizio trovato
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Quick add buttons per categorie */}
                        <div className="mt-3 flex flex-wrap gap-2">
                          {[...new Set(digitalServices.map(s => s.category).filter(Boolean))].slice(0, 5).map(category => (
                            <button
                              key={category}
                              type="button"
                              onClick={() => {
                                setDigitalServiceSearch(category);
                                setShowDigitalServiceDropdown(true);
                              }}
                              className="text-xs bg-white border border-purple-200 text-purple-700 px-2 py-1 rounded hover:bg-purple-100 transition-colors"
                            >
                              {category}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {formData.services.length > 0 && (
                      <div className="space-y-2">
                        {formData.services.map((service, index) => (
                          <div key={index} className="bg-white border border-gray-200 rounded-lg p-3">
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                              <div className="flex-1 min-w-[200px]">
                                <div className="font-medium">{service.description}</div>
                                <div className="text-sm text-gray-600 mt-1">
                                  €{service.price?.toFixed(2)} x {service.quantity}
                                  {service.discount_percent > 0 && (
                                    <span className="text-green-600 font-medium ml-1">(-{service.discount_percent}%)</span>
                                  )}
                                </div>
                                {service.details && (
                                  <div className="text-xs text-gray-500 mt-1 italic truncate max-w-[250px]">
                                    📝 {service.details}
                                  </div>
                                )}
                              </div>
                              
                              {/* Controlli Quantità */}
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-gray-500 mr-1">Qtà:</span>
                                <div className="flex items-center border border-gray-300 rounded">
                                  <button
                                    type="button"
                                    onClick={() => updateServiceQuantity(index, service.quantity - 1)}
                                    className="p-1 hover:bg-gray-100 transition-colors"
                                    disabled={service.quantity <= 1}
                                  >
                                    <Minus className="w-3 h-3 text-gray-600" />
                                  </button>
                                  <span className="px-2 py-0.5 text-sm font-medium min-w-[30px] text-center">
                                    {service.quantity}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => updateServiceQuantity(index, service.quantity + 1)}
                                    className="p-1 hover:bg-gray-100 transition-colors"
                                  >
                                    <Plus className="w-3 h-3 text-gray-600" />
                                  </button>
                                </div>
                              </div>
                              
                              {/* Controllo Sconto Inline */}
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-gray-500">Sconto:</span>
                                <div className="flex items-center">
                                  <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={service.discount_percent || 0}
                                    onChange={(e) => {
                                      const discount = Math.min(100, Math.max(0, parseFloat(e.target.value) || 0));
                                      const updatedServices = [...formData.services];
                                      updatedServices[index] = {
                                        ...service,
                                        discount_percent: discount,
                                        total: service.price * service.quantity * (1 - discount / 100)
                                      };
                                      setFormData(prev => ({ ...prev, services: updatedServices }));
                                    }}
                                    className="w-14 border border-gray-300 rounded px-2 py-1 text-sm text-center"
                                  />
                                  <span className="text-sm text-gray-500 ml-1">%</span>
                                </div>
                              </div>
                              
                              {/* Totale e Azioni */}
                              <div className="flex items-center gap-2">
                                <div className="text-lg font-semibold min-w-[80px] text-right">
                                  {formatCurrency(service.total || (service.price * service.quantity * (1 - (service.discount_percent || 0) / 100)))}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => startEditService(index)}
                                  className="text-blue-600 hover:text-blue-800 p-1"
                                  title="Modifica completa"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removeService(index)}
                                  className="text-red-600 hover:text-red-800 p-1"
                                  title="Elimina"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Altri Campi */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Validità (giorni)
                      </label>
                      <input
                        type="number"
                        value={formData.validity_days}
                        onChange={(e) => setFormData({ ...formData, validity_days: parseInt(e.target.value) || 30 })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        min="1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        💰 Piano di Pagamento
                      </label>
                      <select
                        value={formData.payment_schedule_type}
                        onChange={(e) => setFormData({ ...formData, payment_schedule_type: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white"
                      >
                        <option value="full">Pagamento unico (100%)</option>
                        <option value="split_50_50">50% Acconto + 50% Saldo</option>
                      </select>
                      {formData.payment_schedule_type === 'split_50_50' && (
                        <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                          <p className="font-medium text-amber-800 mb-1">Pagamenti programmati:</p>
                          <ul className="text-amber-700 space-y-1">
                            <li>• <strong>Acconto 50%</strong> - Alla conferma ordine</li>
                            <li>• <strong>Saldo 50%</strong> - A merce pronta per la spedizione</li>
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Termini e Condizioni
                    </label>
                    <select
                      value=""
                      onChange={(e) => {
                        if (e.target.value) {
                          setFormData({ ...formData, terms_and_conditions: e.target.value });
                        }
                      }}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-2 bg-white"
                    >
                      <option value="">-- Seleziona template --</option>
                      <option value="Pagamento anticipato tramite bonifico bancario.&#10;Spese di consegna e imballi da definire in fase di accettazione ordine.">
                        💳 Bonifico anticipato + spese da definire
                      </option>
                      <option value="Pagamento 50% acconto alla conferma ordine, 50% saldo a merce pronta.&#10;Spese di consegna e imballi da definire in fase di accettazione ordine.">
                        💰 50% acconto + 50% saldo
                      </option>
                      <option value="Pagamento a 30 giorni data fattura.&#10;Spese di consegna e imballi incluse per ordini superiori a €500.">
                        📅 Pagamento 30gg + spese incluse
                      </option>
                      <option value="Ordine minimo 24 bottiglie.&#10;Consegna 15 giorni lavorativi.&#10;Pagamento anticipato tramite bonifico bancario.">
                        📦 Ordine minimo + consegna 15gg
                      </option>
                      <option value="Consegna 15gg lavorativi previa conferma grafica e ricetta.&#10;Condizioni di pagamento 50% acconto, 50% a merce pronta per la spedizione.">
                        🎨 Con conferma grafica
                      </option>
                      <option value="Preventivo valido 30 giorni.&#10;Pagamento anticipato tramite bonifico bancario.&#10;Spese di spedizione escluse.">
                        ⏰ Validità 30gg + spedizione esclusa
                      </option>
                    </select>
                    <textarea
                      value={formData.terms_and_conditions}
                      onChange={(e) => setFormData({ ...formData, terms_and_conditions: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      rows="3"
                      placeholder="Seleziona un template o scrivi i termini personalizzati..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Note Admin (private)
                    </label>
                    <textarea
                      value={formData.admin_notes}
                      onChange={(e) => setFormData({ ...formData, admin_notes: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      rows="2"
                    />
                  </div>

                  {/* Opzioni PDF */}
                  <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                    <h3 className="font-semibold mb-3 text-gray-900">⚙️ Opzioni PDF</h3>
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.show_discount_table}
                        onChange={(e) => setFormData({ ...formData, show_discount_table: e.target.checked })}
                        className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900">
                          Mostra tabella sconti quantità nel PDF
                        </span>
                        <p className="text-xs text-gray-500 mt-0.5">
                          La tabella con gli sconti per quantità (24-48, 49-96, etc.) sarà visibile nel PDF
                        </p>
                      </div>
                    </label>
                  </div>

                  {/* Allegati (Mockup Bottiglie) - Solo per preventivi esistenti */}
                  {editingQuote && (
                    <div className="border border-gray-300 rounded-lg p-4">
                      <h3 className="font-semibold mb-3 text-gray-900 flex items-center">
                        <Paperclip className="w-5 h-5 mr-2" />
                        📎 Allegati (Mockup Bottiglie)
                      </h3>
                      
                      {/* Upload Button */}
                      <div className="mb-4">
                        <label className="cursor-pointer inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors">
                          <Upload className="w-4 h-4 mr-2" />
                          {uploadingAttachment ? 'Caricamento...' : 'Carica Allegato'}
                          <input
                            type="file"
                            multiple
                            accept=".jpg,.jpeg,.png,.pdf,.webp,.gif"
                            onChange={(e) => handleFileUpload(e, editingQuote.id)}
                            className="hidden"
                            disabled={uploadingAttachment}
                          />
                        </label>
                        <p className="text-xs text-gray-500 mt-2">
                          Formati supportati: JPG, PNG, PDF, WebP, GIF
                        </p>
                      </div>

                      {/* Attachments List */}
                      {editingQuote.attachments && editingQuote.attachments.length > 0 ? (
                        <div className="space-y-2">
                          {editingQuote.attachments.map((attachment, index) => (
                            <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                              <div className="flex items-center space-x-3">
                                <FileText className="w-5 h-5 text-blue-600" />
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{attachment.filename}</p>
                                  <p className="text-xs text-gray-500">
                                    {attachment.uploaded_at && new Date(attachment.uploaded_at).toLocaleDateString('it-IT')}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <a
                                  href={attachment.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-700"
                                >
                                  <Eye className="w-4 h-4" />
                                </a>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteAttachment(editingQuote.id, index)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">Nessun allegato caricato</p>
                      )}
                    </div>
                  )}

                  {/* Riepilogo Totali */}
                  {(formData.items.length > 0 || formData.services.length > 0) && (() => {
                    const productsTotal = formData.items.reduce((sum, item) => sum + item.total, 0);
                    const servicesTotal = formData.services.reduce((sum, svc) => {
                      const price = svc.price || 0;
                      const qty = svc.quantity || 1;
                      const discount = svc.discount_percent || 0;
                      return sum + (price * qty * (1 - discount / 100));
                    }, 0);
                    const subtotal = productsTotal + servicesTotal;
                    // Costo spedizione: override manuale ha priorità, altrimenti fascia automatica
                    const totalBottles = formData.items.reduce((sum, it) => sum + (Number(it.quantity) || 0), 0);
                    const applicableTier = (discountTiers || []).find(t => {
                      const mn = Number(t.min_quantity || 0);
                      const mx = t.max_quantity == null ? null : Number(t.max_quantity);
                      return totalBottles >= mn && (mx == null || totalBottles <= mx);
                    });
                    const autoShipping = totalBottles > 0 && applicableTier ? Number(applicableTier.shipping_cost || 0) : 0;
                    const isOverride = formData.shipping_override !== null && formData.shipping_override !== undefined;
                    const shippingCost = isOverride ? Number(formData.shipping_override) : autoShipping;
                    const taxable = subtotal + shippingCost;
                    const vat = taxable * 0.22;
                    const grand = taxable + vat;
                    return (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setRiepilogoOpen(o => !o)}
                        data-testid="btn-toggle-riepilogo"
                        className="w-full flex items-center justify-between p-4 hover:bg-amber-100/60 transition-colors text-left"
                      >
                        <div className="flex items-center gap-3">
                          <h3 className="font-semibold text-gray-900">📊 Riepilogo Preventivo</h3>
                          {isOverride && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-600 text-white font-bold uppercase tracking-wide">
                              Spedizione {Number(formData.shipping_override) === 0 ? 'gratuita' : 'personalizzata'}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right leading-tight">
                            <div className="text-[10px] uppercase tracking-wide text-gray-500 font-medium">Totale (IVA incl.)</div>
                            <div className="text-lg font-bold text-amber-700 tabular-nums">{formatCurrency(grand)}</div>
                          </div>
                          {riepilogoOpen ? <ChevronDown className="w-5 h-5 text-gray-500" /> : <ChevronRight className="w-5 h-5 text-gray-500" />}
                        </div>
                      </button>
                      {riepilogoOpen && (
                      <div className="px-4 pb-4 -mt-1 space-y-2 border-t border-amber-200 pt-3">
                        {formData.items.length > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Subtotale prodotti (IVA esclusa):</span>
                            <span className="font-medium">{formatCurrency(productsTotal)}</span>
                          </div>
                        )}
                        {formData.services.length > 0 && (
                          <>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Servizi aggiuntivi (IVA esclusa):</span>
                              <span className="font-medium">{formatCurrency(servicesTotal)}</span>
                            </div>
                            {formData.services.some(svc => svc.discount_percent > 0) && (
                              <div className="text-xs text-green-600 text-right">
                                (inclusi sconti servizi)
                              </div>
                            )}
                          </>
                        )}
                        <div className="flex justify-between text-sm border-t border-amber-300 pt-2">
                          <span className="text-gray-700 font-medium">Totale IVA esclusa (prodotti+servizi):</span>
                          <span className="font-semibold">{formatCurrency(subtotal)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-orange-700 font-medium">
                            🚚 Costo spedizione (IVA esclusa)
                            {isOverride ? (
                              <span className="text-xs text-purple-700 bg-purple-100 px-2 py-0.5 rounded ml-2 font-semibold">
                                {Number(formData.shipping_override) === 0 ? 'GRATUITA (override)' : 'Personalizzata (override)'}
                              </span>
                            ) : (
                              totalBottles > 0 && (
                                <span className="text-xs text-gray-500 ml-2">
                                  {applicableTier ? `— fascia ${applicableTier.label} · ${totalBottles} bot.` : `— nessuna fascia per ${totalBottles} bot.`}
                                </span>
                              )
                            )}
                          </span>
                          <span className="font-semibold text-orange-700">{formatCurrency(shippingCost)}</span>
                        </div>
                        {/* Controlli override spedizione */}
                        <div className="bg-white/60 border border-orange-200 rounded p-3 mt-1">
                          <div className="flex items-center gap-3 flex-wrap text-xs">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isOverride}
                                onChange={(e) => setFormData({...formData, shipping_override: e.target.checked ? 0 : null})}
                                className="w-4 h-4 accent-orange-600"
                                data-testid="quote-shipping-override-toggle"
                              />
                              <span className="font-semibold text-gray-800">Personalizza / escludi spedizione</span>
                            </label>
                            {isOverride && (
                              <>
                                <div className="flex items-center gap-1">
                                  <span className="text-gray-500">€</span>
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={formData.shipping_override ?? 0}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      setFormData({...formData, shipping_override: v === '' ? 0 : parseFloat(v)});
                                    }}
                                    data-testid="quote-shipping-override-input"
                                    className="w-24 px-2 py-1 border border-gray-300 rounded text-sm tabular-nums focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setFormData({...formData, shipping_override: 0})}
                                  data-testid="quote-shipping-free-btn"
                                  className="px-2 py-1 text-xs bg-green-100 text-green-700 hover:bg-green-200 rounded font-medium"
                                >
                                  Spedizione gratuita
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setFormData({...formData, shipping_override: null})}
                                  data-testid="quote-shipping-auto-btn"
                                  className="px-2 py-1 text-xs bg-gray-100 text-gray-700 hover:bg-gray-200 rounded font-medium"
                                >
                                  Ripristina automatico
                                </button>
                              </>
                            )}
                          </div>
                          {isOverride && (
                            <p className="text-xs text-gray-500 mt-2">
                              Il valore automatico sarebbe stato <strong>{formatCurrency(autoShipping)}</strong> (fascia {applicableTier?.label || `${totalBottles} bot.`}). Sarà usato invece <strong>{formatCurrency(shippingCost)}</strong>.
                            </p>
                          )}
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">IVA 22%:</span>
                          <span className="font-medium">{formatCurrency(vat)}</span>
                        </div>
                        <div className="flex justify-between text-lg border-t-2 border-amber-400 pt-2">
                          <span className="text-gray-900 font-bold">TOTALE IVA INCLUSA:</span>
                          <span className="font-bold text-amber-600">{formatCurrency(grand)}</span>
                        </div>
                        <div className="text-xs text-gray-500 mt-2">
                          💡 La spedizione viene calcolata automaticamente al salvataggio in base al totale bottiglie. Puoi gestire le fasce in{' '}
                          <a href="/admin/shipping-costs" target="_blank" rel="noreferrer" className="text-purple-600 underline">Costi Spedizioni</a>.
                        </div>
                        {/* MOL aggregato (margine operativo lordo) */}
                        {(() => {
                          const revenue = formData.items.reduce((sum, item) => sum + (item.total || 0), 0);
                          const totalCost = formData.items.reduce((sum, item) => sum + ((item.unit_cost || 0) * (item.quantity || 0)), 0);
                          const itemsWithCost = formData.items.filter(it => (it.unit_cost || 0) > 0).length;
                          const fiscal = computeFiscalSummary(formData.items, 'alcohol_content');
                          const hasFiscal = fiscal.accisa > 0 || fiscal.contrassegno > 0;
                          if (itemsWithCost === 0 && !hasFiscal) return null;
                          const molTotalBottles = fiscal.totalBottles;
                          const molNet = revenue - totalCost - fiscal.accisa - fiscal.contrassegno;
                          const molNetPct = revenue > 0 ? (molNet / revenue) * 100 : 0;
                          const color = molNet >= 0 ? 'text-green-700' : 'text-red-600';
                          return (
                            <>
                              {/* Breakdown per-riga MOL — utile con prodotti in omaggio */}
                              <div className="mt-3 pt-3 border-t border-dashed border-amber-300">
                                <div className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                  🔎 <span>MOL dettagliato per prodotto</span>
                                  <span className="text-[10px] font-normal text-gray-500">(accisa/contrassegno/costo industriale applicati anche agli omaggi)</span>
                                </div>
                                <div className="overflow-x-auto">
                                  <table className="w-full text-[11px]" data-testid="quote-mol-per-line">
                                    <thead>
                                      <tr className="text-gray-500 border-b border-gray-200">
                                        <th className="text-left font-semibold py-1 pr-2">Prodotto</th>
                                        <th className="text-right font-semibold py-1 px-1">Qty</th>
                                        <th className="text-right font-semibold py-1 px-1">Ricavo</th>
                                        <th className="text-right font-semibold py-1 px-1">Ind.</th>
                                        <th className="text-right font-semibold py-1 px-1">Accisa</th>
                                        <th className="text-right font-semibold py-1 px-1">Contr.</th>
                                        <th className="text-right font-semibold py-1 pl-1">Netto</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {formData.items.map((it, idx) => {
                                        const qty = Number(it.quantity) || 0;
                                        const rev = Number(it.total) || 0;
                                        const indCost = (Number(it.unit_cost) || 0) * qty;
                                        const accisa = computeAccisaPerBottle(it.format, it.alcohol_content) * qty;
                                        const contr = computeContrassegnoPerBottle(it.format) * qty;
                                        const net = rev - indCost - accisa - contr;
                                        const isGift = qty > 0 && rev === 0;
                                        const netColor = net >= 0 ? 'text-green-700' : 'text-red-600';
                                        return (
                                          <tr key={idx} className={`border-b border-gray-100 ${isGift ? 'bg-purple-50/60' : ''}`} data-testid={`mol-line-${idx}`}>
                                            <td className="py-1 pr-2 text-gray-800">
                                              <div className="flex items-center gap-1.5">
                                                <span className="truncate max-w-[200px]">{it.description || `Riga ${idx + 1}`}</span>
                                                {isGift && (
                                                  <span className="inline-block px-1.5 py-0.5 rounded bg-purple-600 text-white text-[9px] font-bold uppercase tracking-wide" data-testid={`mol-line-gift-${idx}`}>
                                                    Omaggio
                                                  </span>
                                                )}
                                              </div>
                                              <div className="text-[10px] text-gray-500">{it.format} · {(it.alcohol_content || 0)}° alc</div>
                                            </td>
                                            <td className="text-right py-1 px-1 tabular-nums">{qty}</td>
                                            <td className="text-right py-1 px-1 tabular-nums text-gray-800">{formatCurrency(rev)}</td>
                                            <td className="text-right py-1 px-1 tabular-nums text-gray-700">{formatCurrencyMillesimi(indCost)}</td>
                                            <td className="text-right py-1 px-1 tabular-nums text-gray-700">{formatCurrencyMillesimi(accisa)}</td>
                                            <td className="text-right py-1 px-1 tabular-nums text-gray-700">{formatCurrencyMillesimi(contr)}</td>
                                            <td className={`text-right py-1 pl-1 tabular-nums font-bold ${netColor}`}>{formatCurrency(net)}</td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </div>

                              {itemsWithCost > 0 && revenue > 0 && (
                                <div className={`flex justify-between text-sm pt-2 mt-1 border-t border-dashed ${color}`} data-testid="quote-modal-mol">
                                  <span className="font-medium">MOL netto (al netto di costi + accisa + contrassegno):</span>
                                  <span className="font-bold">{formatCurrency(molNet)} <span className="text-xs">({molNetPct.toFixed(1)}%)</span></span>
                                </div>
                              )}
                              {itemsWithCost > 0 && (
                                <div className="flex justify-between text-sm text-gray-700" data-testid="quote-modal-industrial-costs">
                                  <span className="font-medium">Costi industriali ({molTotalBottles} {molTotalBottles === 1 ? 'bottiglia' : 'bottiglie'}):</span>
                                  <span className="font-semibold">{formatCurrencyMillesimi(totalCost)}</span>
                                </div>
                              )}
                              {fiscal.accisa > 0 && (
                                <div className="flex justify-between text-sm text-gray-700" data-testid="quote-modal-accisa">
                                  <span className="font-medium">Accisa (€10,3552/L alcol puro):</span>
                                  <span className="font-semibold">{formatCurrencyMillesimi(fiscal.accisa)}</span>
                                </div>
                              )}
                              {fiscal.contrassegno > 0 && (
                                <div className="flex justify-between text-sm text-gray-700" data-testid="quote-modal-contrassegno">
                                  <span className="font-medium">Contrassegno di stato ({molTotalBottles} {molTotalBottles === 1 ? 'bottiglia' : 'bottiglie'}):</span>
                                  <span className="font-semibold">{formatCurrencyMillesimi(fiscal.contrassegno)}</span>
                                </div>
                              )}
                              {(itemsWithCost > 0 || hasFiscal) && (
                                <div className="flex justify-between text-sm pt-1 mt-1 border-t border-gray-300 text-gray-900" data-testid="quote-modal-total-costs">
                                  <span className="font-semibold">Totale costi (industriali + accisa + contrassegno):</span>
                                  <span className="font-bold">{formatCurrencyMillesimi(totalCost + fiscal.accisa + fiscal.contrassegno)}</span>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                      )}
                    </div>
                    );
                  })()}

                  {/* Bottoni Azione Step 1 */}
                  <div className="flex justify-end space-x-3 pt-4 border-t">
                    <button
                      type="button"
                      onClick={() => {
                        setShowModal(false);
                        resetForm();
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Annulla
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors"
                    >
                      {editingQuote ? 'Salva Modifiche' : 'Avanti: Carica Allegati →'}
                    </button>
                  </div>
                </form>
                )}

                {/* STEP 2: Caricamento Allegati */}
                {!editingQuote && creationStep === 2 && newlyCreatedQuoteId && (
                  <div className="space-y-6">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                      <p className="text-green-800 text-sm">
                        ✅ Preventivo creato con successo! Ora puoi caricare i mockup delle bottiglie.
                      </p>
                    </div>

                    {/* Sezione Upload */}
                    <div className="border border-gray-300 rounded-lg p-6">
                      <h3 className="font-semibold mb-4 text-gray-900 flex items-center text-lg">
                        <Paperclip className="w-5 h-5 mr-2" />
                        📎 Carica Mockup Bottiglie
                      </h3>
                      
                      <div className="mb-6">
                        <label className="cursor-pointer inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-lg font-medium">
                          <Upload className="w-5 h-5 mr-2" />
                          {uploadingAttachment ? 'Caricamento in corso...' : 'Seleziona File'}
                          <input
                            type="file"
                            multiple
                            accept=".jpg,.jpeg,.png,.pdf,.webp,.gif"
                            onChange={(e) => handleFileUpload(e, newlyCreatedQuoteId)}
                            className="hidden"
                            disabled={uploadingAttachment}
                          />
                        </label>
                        <p className="text-sm text-gray-500 mt-3">
                          Formati supportati: JPG, PNG, PDF, WebP, GIF • Upload multiplo consentito
                        </p>
                      </div>

                      {/* Lista allegati caricati */}
                      {quotes.find(q => q.id === newlyCreatedQuoteId)?.attachments?.length > 0 ? (
                        <div className="space-y-2">
                          <h4 className="font-medium text-gray-700 mb-2">File caricati:</h4>
                          {quotes.find(q => q.id === newlyCreatedQuoteId).attachments.map((attachment, index) => (
                            <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                              <div className="flex items-center space-x-3">
                                <FileText className="w-5 h-5 text-blue-600" />
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{attachment.filename}</p>
                                  <p className="text-xs text-gray-500">
                                    {attachment.uploaded_at && new Date(attachment.uploaded_at).toLocaleDateString('it-IT')}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <a
                                  href={attachment.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-700"
                                >
                                  <Eye className="w-4 h-4" />
                                </a>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteAttachment(newlyCreatedQuoteId, index)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                          <Package className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-500">Nessun allegato caricato</p>
                          <p className="text-sm text-gray-400 mt-1">Puoi saltare questo step se non hai mockup</p>
                        </div>
                      )}
                    </div>

                    {/* Bottoni Step 2 */}
                    <div className="flex justify-between pt-4 border-t">
                      <button
                        type="button"
                        onClick={() => setCreationStep(1)}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        ← Indietro
                      </button>
                      <div className="space-x-3">
                        <button
                          type="button"
                          onClick={() => {
                            setShowModal(false);
                            resetForm();
                            loadQuotes();
                          }}
                          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          Salta e Chiudi
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            // Invia email automaticamente
                            const quote = quotes.find(q => q.id === newlyCreatedQuoteId);
                            if (quote) {
                              await handleSendEmail(quote.id, quote.quote_number, quote.customer_data.email);
                            }
                            setShowModal(false);
                            resetForm();
                            loadQuotes();
                          }}
                          className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium"
                        >
                          📧 Invia al Cliente
                        </button>
                      </div>
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        )}

        {/* Modal Visualizzazione Dettaglio */}
        {viewingQuote && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h2 className="text-2xl font-bold">Preventivo {viewingQuote.quote_number}</h2>
                    <div className="mt-2">{getStatusBadge(viewingQuote.status)}</div>
                  </div>
                  <button
                    onClick={() => setViewingQuote(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Info Cliente */}
                  <div className="border-t pt-4">
                    <h3 className="font-semibold mb-2">Cliente</h3>
                    <p className="text-gray-800">{viewingQuote.customer_data.business_name}</p>
                    <p className="text-sm text-gray-600">{viewingQuote.customer_data.email}</p>
                    {viewingQuote.customer_data.vat_number && (
                      <p className="text-sm text-gray-600">P.IVA: {viewingQuote.customer_data.vat_number}</p>
                    )}
                  </div>

                  {/* Date */}
                  <div className="grid grid-cols-2 gap-4 border-t pt-4">
                    <div>
                      <p className="text-sm text-gray-600">Data Emissione</p>
                      <p className="font-medium">{formatDate(viewingQuote.issue_date)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Scadenza</p>
                      <p className="font-medium">{formatDate(viewingQuote.expiry_date)}</p>
                    </div>
                  </div>

                  {/* Prodotti */}
                  <div className="border-t pt-4">
                    <h3 className="font-semibold mb-3">Prodotti</h3>
                    <div className="space-y-2">
                      {viewingQuote.items.map((item, index) => (
                        <div key={index} className="bg-gray-50 p-3 rounded">
                          <div className="flex justify-between">
                            <div>
                              <p className="font-medium">{item.description}</p>
                              <p className="text-sm text-gray-600">
                                {item.format} × {item.quantity} @ {formatCurrency(item.unit_price)}
                                {item.discount_percent > 0 && ` (-${item.discount_percent}%)`}
                              </p>
                            </div>
                            <p className="font-semibold">{formatCurrency(item.total)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Servizi */}
                  {viewingQuote.services && viewingQuote.services.length > 0 && (
                    <div className="border-t pt-4">
                      <h3 className="font-semibold mb-3">Servizi Aggiuntivi</h3>
                      <div className="space-y-2">
                        {viewingQuote.services.map((service, index) => (
                          <div key={index} className="bg-gray-50 p-3 rounded flex justify-between">
                            <p>{service.description}</p>
                            <p className="font-semibold">{formatCurrency(service.price)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Totali */}
                  <div className="border-t pt-4 space-y-2">
                    <div className="flex justify-between text-gray-600">
                      <span>Subtotale (IVA esclusa)</span>
                      <span>{formatCurrency(viewingQuote.subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-gray-600">
                      <span>IVA 22%</span>
                      <span>{formatCurrency(viewingQuote.vat_amount)}</span>
                    </div>
                    <div className="flex justify-between text-xl font-bold pt-2 border-t">
                      <span>Totale</span>
                      <span>{formatCurrency(viewingQuote.total)}</span>
                    </div>
                  </div>

                  {/* Termini */}
                  {viewingQuote.terms_and_conditions && (
                    <div className="border-t pt-4">
                      <h3 className="font-semibold mb-2">Termini e Condizioni</h3>
                      <p className="text-sm text-gray-600">{viewingQuote.terms_and_conditions}</p>
                    </div>
                  )}

                  {/* Note */}
                  {viewingQuote.admin_notes && (
                    <div className="border-t pt-4">
                      <h3 className="font-semibold mb-2">Note Admin</h3>
                      <p className="text-sm text-gray-600">{viewingQuote.admin_notes}</p>
                    </div>
                  )}

                  {viewingQuote.customer_notes && (
                    <div className="border-t pt-4">
                      <h3 className="font-semibold mb-2">Note Cliente</h3>
                      <p className="text-sm text-gray-600">{viewingQuote.customer_notes}</p>
                    </div>
                  )}

                  {/* Allegati */}
                  {viewingQuote.attachments && viewingQuote.attachments.length > 0 && (
                    <div className="border-t pt-4">
                      <h3 className="font-semibold mb-3 flex items-center">
                        <Paperclip className="w-4 h-4 mr-2" />
                        Allegati ({viewingQuote.attachments.length})
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {viewingQuote.attachments.map((attachment, index) => (
                          <a
                            key={index}
                            href={attachment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center space-x-2 p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{attachment.filename}</p>
                              <p className="text-xs text-gray-500">
                                {attachment.uploaded_at && new Date(attachment.uploaded_at).toLocaleDateString('it-IT')}
                              </p>
                            </div>
                            <Eye className="w-4 h-4 text-blue-600 flex-shrink-0" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Azioni */}
                  <div className="border-t pt-4 flex justify-end space-x-3">
                    <button
                      onClick={() => handleSendEmail(viewingQuote.id, viewingQuote.quote_number, viewingQuote.customer_data.email)}
                      className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                    >
                      <Mail className="w-4 h-4" />
                      <span>Invia Email</span>
                    </button>
                    <button
                      onClick={() => handleExportPDF(viewingQuote.id, viewingQuote.quote_number)}
                      className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      <span>Scarica PDF</span>
                    </button>
                    <button
                      onClick={() => setViewingQuote(null)}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Chiudi
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminQuotes;
